import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchGetMassMessagingGroups } from 'api/mass-messaging/groups';
import { fetchGetMassMessagingTasks } from 'api/mass-messaging/tasks';
import { dispatch, store } from 'store';
import { MassMessagingGroupList } from 'types/mass-messaging/groups';
import { MassMessagingTaskType } from 'types/mass-messaging/tasks';

const initialState: {
  groups: MassMessagingGroupList;
  tasks: Array<MassMessagingTaskType>;
  tasksLoading: boolean;
  tasksHasMore: boolean;
  tasksCurrentPage: number;
} = {
  groups: {
    groups: [],
    detailsList: [],
  },
  tasks: [],
  tasksLoading: false,
  tasksHasMore: true,
  tasksCurrentPage: 0,
};

const massMessagingSlice = createSlice({
  name: 'massMessaging',
  initialState,
  reducers: {
    storeSetGroups: (state, action: PayloadAction<MassMessagingGroupList>) => {
      state.groups = action.payload;
    },
    storeSetTasks: (state, action: PayloadAction<Array<MassMessagingTaskType>>) => {
      state.tasks = action.payload;
    },
    storeAppendTasks: (state, action: PayloadAction<Array<MassMessagingTaskType>>) => {
      state.tasks = [...state.tasks, ...action.payload];
    },
    storeSetTasksLoading: (state, action: PayloadAction<boolean>) => {
      state.tasksLoading = action.payload;
    },
    storeSetTasksHasMore: (state, action: PayloadAction<boolean>) => {
      state.tasksHasMore = action.payload;
    },
    storeSetTasksCurrentPage: (state, action: PayloadAction<number>) => {
      state.tasksCurrentPage = action.payload;
    },
    storeResetTasksPagination: (state) => {
      state.tasks = [];
      state.tasksCurrentPage = 0;
      state.tasksHasMore = true;
    },
  },
});
export default massMessagingSlice.reducer;
export const {
  storeSetGroups,
  storeSetTasks,
  storeAppendTasks,
  storeSetTasksLoading,
  storeSetTasksHasMore,
  storeSetTasksCurrentPage,
  storeResetTasksPagination,
} = massMessagingSlice.actions;

//* Groups actions
export const setMassMessagingGroups = (groups: MassMessagingGroupList) => {
  dispatch(storeSetGroups(groups));
};
export const getMassMessagingGroups = async () => {
  try {
    const data = await fetchGetMassMessagingGroups();
    if (data?.result) {
      setMassMessagingGroups({
        groups: data.companyMassMessagingGroups ?? [],
        detailsList: data.groupDetailsList ?? [],
      });
    }
  } catch (error) {
    console.warn('Error in getMassMessagingGroups:', error);
  }
};

//* Tasks actions
export const setMassMessagingTasks = (tasks: Array<MassMessagingTaskType>) => {
  dispatch(storeSetTasks(tasks));
};

export const appendMassMessagingTasks = (tasks: Array<MassMessagingTaskType>) => {
  dispatch(storeAppendTasks(tasks));
};

export const setTasksLoading = (loading: boolean) => {
  dispatch(storeSetTasksLoading(loading));
};

export const setTasksHasMore = (hasMore: boolean) => {
  dispatch(storeSetTasksHasMore(hasMore));
};

export const setTasksCurrentPage = (page: number) => {
  dispatch(storeSetTasksCurrentPage(page));
};

export const resetTasksPagination = () => {
  dispatch(storeResetTasksPagination());
};

export const getMassMessagingTasks = async (isLoadMore: boolean = false, options?: { silent?: boolean }) => {
  try {
    const currentState = store.getState().massMessaging;

    if (!isLoadMore && !options?.silent) {
      // טעינה ראשונית - איפוס. silent = רענון רקע (polling) — בלי להבהב ספינר.
      resetTasksPagination();
      setTasksLoading(true);
    }

    const skip = isLoadMore ? currentState.tasks.length : 0;
    // רענון שקט (polling) מושך בקריאה אחת את כל מה שכבר טעון — החלפה לעמוד ראשון
    // בלבד קיצצה את הרשימה, וה-grid הגלול ירה loadMore-ים מיידיים (skip=100,200...)
    // אחרי כל tick. תקרה 500 כדי לא לגדול בלי סוף.
    const size = options?.silent ? Math.min(500, Math.max(100, currentState.tasks.length)) : 100;
    const data = await fetchGetMassMessagingTasks(skip, size);

    if (data && data.data) {
      if (isLoadMore) {
        appendMassMessagingTasks(data.data);
      } else {
        setMassMessagingTasks(data.data);
      }

      // בדיקה אם יש עוד נתונים
      const hasMore = data.data.length === size;
      setTasksHasMore(hasMore);
    } else {
      setTasksHasMore(false);
    }
  } catch {
    setTasksHasMore(false);
  } finally {
    if (!isLoadMore) {
      setTasksLoading(false);
    } else {
      setTasksLoading(false);
    }
  }
};
export const removeMassMessagingTask = async (id: number) => {
  const newTasks = store.getState().massMessaging.tasks.filter((task) => task.id !== id);
  setMassMessagingTasks(newTasks);
};
